/**
 * Escalation questions on the chat's side of the wire (agion/Agion#3427):
 * which refusal governance returned for an answer, what each refusal says,
 * and the recorded answer as the question's own words. Governance validates
 * every answer; nothing here checks option ids or lengths.
 */

import type { AnswerRefusal, ChoiceOrTextQuestion, Escalation, EscalationAnswer, EscalationQuestion } from '../types/escalations'
import { isChatApiError } from './api-failure'

type Translate = (key: string, fallback: string) => string

/** What a refusal of an answer says, in both Escalations sections. */
export function answerRefusalText(refusal: AnswerRefusal, t: Translate): string {
  switch (refusal) {
    case 'invalid':
      return t('escalations.question.error.invalid', 'The question does not accept this answer.')
    case 'expired':
      return t('escalations.question.error.expired', 'The question expired before the answer arrived.')
    case 'already_answered':
      return t('escalations.question.error.alreadyAnswered', 'Someone answered this question first.')
    case 'forbidden':
      return t('escalations.question.error.forbidden', 'You cannot answer this question.')
    case 'ticket_mismatch':
      return t('escalations.question.error.ticketMismatch', 'The answer did not match your session. Reload the page and try again.')
    case 'ticket_expired':
      return t('escalations.question.error.ticketExpired', 'The answer took too long to send. Try again.')
    case 'run_not_live':
      return t('escalations.question.error.runNotLive', 'The run has ended, so it cannot take the answer.')
    case 'host_not_connected':
      return t('escalations.question.error.hostNotConnected', 'The host running this mission is not connected. Try again later.')
    case 'malformed':
      return t('escalations.question.error.malformed', 'The answer could not be read. Try again.')
    case 'unreachable':
      return t('escalations.question.error.unreachable', 'Could not reach the run. Try again.')
    case 'unconfirmed':
      return t('escalations.question.error.unconfirmed', 'The run has the answer, but it is not recorded yet. Check again shortly.')
    default:
      return t('escalations.question.error.failed', 'The answer was not sent. Try again.')
  }
}

/**
 * Whether an answered question's answer is kept with the run: every form
 * answer, and a free-text answer recorded without its text (owner ruling
 * 2026-10-25). A free-text row answered before the ruling still carries the
 * text and shows it.
 */
export function answerKeptWithRun(escalation: Escalation, question: EscalationQuestion): boolean {
  if (escalation.status !== 'answered') return false
  return question.type === 'form' || (question.type === 'free_text' && !escalation.answer?.text)
}

/** When the question was answered, in the reader's locale; undefined without a valid time. */
export function answeredAtText(answeredAt: string | undefined): string | undefined {
  if (!answeredAt) return undefined
  const at = new Date(answeredAt)
  return Number.isNaN(at.getTime()) ? undefined : at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** The refusal an answer POST failed with, from the server's status and code. */
export function answerRefusalOf(error: unknown): AnswerRefusal {
  if (!isChatApiError(error)) return 'failed'
  if (error.status === 403) return 'forbidden'
  switch (error.code) {
    case 'ESCALATION_ANSWER_INVALID':
      return 'invalid'
    case 'ESCALATION_EXPIRED':
      return 'expired'
    case 'ESCALATION_ALREADY_ANSWERED':
      return 'already_answered'
    default:
      return 'failed'
  }
}

/**
 * The recorded answer as a person reads it: the label of the chosen option
 * for a single-choice question, the text for a free-text one. A form's values
 * stay on the Edge, so a form gives undefined. An id the question does not
 * offer gives undefined, so no raw id reaches the screen.
 */
export function recordedAnswerText(
  question: EscalationQuestion,
  answer: EscalationAnswer | undefined,
): string | undefined {
  if (!answer) return undefined
  if (question.type === 'single_choice') {
    const chosen = answer.selected_option_ids?.[0]
    return question.options?.find((option) => option.id === chosen)?.label
  }
  if (question.type === 'form') return undefined
  return answer.text || undefined
}

/** Whether the question is answered with its choices or a text box (not a form). */
export function isChoiceOrTextQuestion(question: EscalationQuestion | undefined): question is ChoiceOrTextQuestion {
  return question?.type === 'single_choice' || question?.type === 'free_text'
}
